import React, { useState } from 'react';
import { Palette, Droplet, Square, Move, Type, Image, Sliders } from 'lucide-react';

interface StyleEditorProps {
  styles: Record<string, any>;
  onChange: (styles: Record<string, any>) => void;
}

export const StyleEditor: React.FC<StyleEditorProps> = ({ styles = {}, onChange }) => {
  const [activeSection, setActiveSection] = useState<string>('colors');

  const handleStyleChange = (key: string, value: any) => {
    onChange({ ...styles, [key]: value });
  };

  // Predefined color palette
  const colorPalette = [
    '#FFFFFF', '#F3F4F6', '#E5E7EB', '#D1D5DB',
    '#000000', '#1F2937', '#374151', '#4B5563',
    '#EF4444', '#F59E0B', '#10B981', '#3B82F6',
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'
  ];

  const spacingOptions = ['0', '1', '2', '4', '8', '12', '16', '20', '24', '32', '40', '48', '56', '64'];

  const fontSizes = [
    { label: 'XS', value: '12px' },
    { label: 'SM', value: '14px' },
    { label: 'Base', value: '16px' },
    { label: 'LG', value: '18px' },
    { label: 'XL', value: '20px' },
    { label: '2XL', value: '24px' },
    { label: '3XL', value: '30px' },
    { label: '4XL', value: '36px' },
    { label: '5XL', value: '48px' }
  ];

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'colors', icon: Palette, label: 'Colors' },
          { id: 'spacing', icon: Square, label: 'Spacing' },
          { id: 'typography', icon: Type, label: 'Typography' },
          { id: 'background', icon: Image, label: 'Background' },
          { id: 'effects', icon: Sliders, label: 'Effects' }
        ].map(section => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                activeSection === section.id
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {section.label}
            </button>
          );
        })}
      </div>

      {/* Colors Section */}
      {activeSection === 'colors' && (
        <div className="space-y-4">
          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium mb-2">Background Color</label>
            <div className="space-y-2">
              <input
                type="color"
                value={styles.backgroundColor || '#FFFFFF'}
                onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                className="w-full h-10 rounded cursor-pointer"
              />
              <div className="grid grid-cols-8 gap-1">
                {colorPalette.map(color => (
                  <button
                    key={color}
                    onClick={() => handleStyleChange('backgroundColor', color)}
                    className="w-full h-8 rounded border border-gray-300 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              <input
                type="text"
                value={styles.backgroundColor || ''}
                onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                placeholder="#FFFFFF or rgb(255,255,255)"
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>
          </div>

          {/* Text Color */}
          <div>
            <label className="block text-sm font-medium mb-2">Text Color</label>
            <div className="space-y-2">
              <input
                type="color"
                value={styles.color || '#000000'}
                onChange={(e) => handleStyleChange('color', e.target.value)}
                className="w-full h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={styles.color || ''}
                onChange={(e) => handleStyleChange('color', e.target.value)}
                placeholder="#000000"
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>
          </div>

          {/* Accent Color */}
          <div>
            <label className="block text-sm font-medium mb-2">Accent Color</label>
            <input
              type="color"
              value={styles.accentColor || '#3B82F6'}
              onChange={(e) => handleStyleChange('accentColor', e.target.value)}
              className="w-full h-10 rounded cursor-pointer"
            />
          </div>
        </div>
      )}

      {/* Spacing Section */}
      {activeSection === 'spacing' && (
        <div className="space-y-4">
          {/* Padding */}
          <div>
            <label className="block text-sm font-medium mb-2">Padding</label>
            <div className="grid grid-cols-2 gap-2">
              {['Top', 'Right', 'Bottom', 'Left'].map(side => (
                <div key={side}>
                  <label className="text-xs text-gray-600">{side}</label>
                  <select
                    value={styles[`padding${side}`] || 0}
                    onChange={(e) => handleStyleChange(`padding${side}`, parseInt(e.target.value))}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    {spacingOptions.map(option => (
                      <option key={option} value={option}>{option}px</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                const value = styles.paddingTop || 16;
                handleStyleChange('paddingTop', value);
                handleStyleChange('paddingRight', value);
                handleStyleChange('paddingBottom', value);
                handleStyleChange('paddingLeft', value);
              }}
              className="mt-2 text-sm text-blue-600 hover:underline"
            >
              Apply to all sides
            </button>
          </div>

          {/* Margin */}
          <div>
            <label className="block text-sm font-medium mb-2">Margin</label>
            <div className="grid grid-cols-2 gap-2">
              {['Top', 'Right', 'Bottom', 'Left'].map(side => (
                <div key={side}>
                  <label className="text-xs text-gray-600">{side}</label>
                  <select
                    value={styles[`margin${side}`] || 0}
                    onChange={(e) => handleStyleChange(`margin${side}`, parseInt(e.target.value))}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    {spacingOptions.map(option => (
                      <option key={option} value={option}>{option}px</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Gap (for flex/grid) */}
          <div>
            <label className="block text-sm font-medium mb-2">Gap</label>
            <select
              value={styles.gap || 0}
              onChange={(e) => handleStyleChange('gap', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {spacingOptions.map(option => (
                <option key={option} value={option}>{option}px</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Typography Section */}
      {activeSection === 'typography' && (
        <div className="space-y-4">
          {/* Font Size */}
          <div>
            <label className="block text-sm font-medium mb-2">Font Size</label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {fontSizes.map(size => (
                <button
                  key={size.value}
                  onClick={() => handleStyleChange('fontSize', size.value)}
                  className={`px-2 py-1 text-sm rounded border ${
                    styles.fontSize === size.value
                      ? 'bg-blue-100 border-blue-500'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {size.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={styles.fontSize || ''}
              onChange={(e) => handleStyleChange('fontSize', e.target.value)}
              placeholder="Custom size (e.g., 18px, 1.5rem)"
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>

          {/* Font Weight */}
          <div>
            <label className="block text-sm font-medium mb-2">Font Weight</label>
            <select
              value={styles.fontWeight || 'normal'}
              onChange={(e) => handleStyleChange('fontWeight', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="100">Thin (100)</option>
              <option value="300">Light (300)</option>
              <option value="normal">Normal (400)</option>
              <option value="500">Medium (500)</option>
              <option value="600">Semi Bold (600)</option>
              <option value="bold">Bold (700)</option>
              <option value="900">Black (900)</option>
            </select>
          </div>

          {/* Text Align */}
          <div>
            <label className="block text-sm font-medium mb-2">Text Align</label>
            <div className="grid grid-cols-4 gap-2">
              {['left', 'center', 'right', 'justify'].map(align => (
                <button
                  key={align}
                  onClick={() => handleStyleChange('textAlign', align)}
                  className={`px-3 py-2 text-sm rounded border capitalize ${
                    styles.textAlign === align
                      ? 'bg-blue-100 border-blue-500'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {align}
                </button>
              ))}
            </div>
          </div>

          {/* Line Height */}
          <div>
            <label className="block text-sm font-medium mb-2">Line Height</label>
            <select
              value={styles.lineHeight || '1.5'}
              onChange={(e) => handleStyleChange('lineHeight', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="1">1.0 - Tight</option>
              <option value="1.25">1.25 - Snug</option>
              <option value="1.5">1.5 - Normal</option>
              <option value="1.75">1.75 - Relaxed</option>
              <option value="2">2.0 - Loose</option>
            </select>
          </div>
        </div>
      )}

      {/* Background Section */}
      {activeSection === 'background' && (
        <div className="space-y-4">
          {/* Background Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Background Type</label>
            <select
              value={styles.backgroundType || 'solid'}
              onChange={(e) => handleStyleChange('backgroundType', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="solid">Solid Color</option>
              <option value="gradient">Gradient</option>
              <option value="image">Image</option>
            </select>
          </div>

          {/* Gradient */}
          {styles.backgroundType === 'gradient' && (
            <div>
              <label className="block text-sm font-medium mb-2">Gradient</label>
              <select
                value={styles.gradient || 'linear'}
                onChange={(e) => handleStyleChange('gradient', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg mb-2"
              >
                <option value="linear">Linear</option>
                <option value="radial">Radial</option>
              </select>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-600">From</label>
                  <input
                    type="color"
                    value={styles.gradientFrom || '#3B82F6'}
                    onChange={(e) => handleStyleChange('gradientFrom', e.target.value)}
                    className="w-full h-10 rounded cursor-pointer"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">To</label>
                  <input
                    type="color"
                    value={styles.gradientTo || '#8B5CF6'}
                    onChange={(e) => handleStyleChange('gradientTo', e.target.value)}
                    className="w-full h-10 rounded cursor-pointer"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Background Image */}
          {styles.backgroundType === 'image' && (
            <div>
              <label className="block text-sm font-medium mb-2">Background Image</label>
              <input
                type="text"
                value={styles.backgroundImage || ''}
                onChange={(e) => handleStyleChange('backgroundImage', e.target.value)}
                placeholder="Image URL"
                className="w-full px-3 py-2 border rounded-lg mb-2"
              />
              <button className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
                Upload Image
              </button>
            </div>
          )}
        </div>
      )}

      {/* Effects Section */}
      {activeSection === 'effects' && (
        <div className="space-y-4">
          {/* Border Radius */}
          <div>
            <label className="block text-sm font-medium mb-2">Border Radius</label>
            <input
              type="range"
              min="0"
              max="50"
              value={styles.borderRadius || 0}
              onChange={(e) => handleStyleChange('borderRadius', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600">
              {styles.borderRadius || 0}px
            </div>
          </div>

          {/* Box Shadow */}
          <div>
            <label className="block text-sm font-medium mb-2">Box Shadow</label>
            <select
              value={styles.boxShadow || 'none'}
              onChange={(e) => handleStyleChange('boxShadow', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="none">None</option>
              <option value="0 1px 3px rgba(0,0,0,0.12)">Small</option>
              <option value="0 4px 6px rgba(0,0,0,0.1)">Medium</option>
              <option value="0 10px 15px rgba(0,0,0,0.1)">Large</option>
              <option value="0 20px 25px rgba(0,0,0,0.1)">Extra Large</option>
            </select>
          </div>

          {/* Opacity */}
          <div>
            <label className="block text-sm font-medium mb-2">Opacity</label>
            <input
              type="range"
              min="0"
              max="100"
              value={(parseFloat(styles.opacity) || 1) * 100}
              onChange={(e) => handleStyleChange('opacity', parseFloat(e.target.value) / 100)}
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600">
              {Math.round((parseFloat(styles.opacity) || 1) * 100)}%
            </div>
          </div>

          {/* Border */}
          <div>
            <label className="block text-sm font-medium mb-2">Border</label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                value={styles.borderWidth || 0}
                onChange={(e) => handleStyleChange('borderWidth', parseInt(e.target.value))}
                placeholder="Width"
                className="px-3 py-2 border rounded-lg"
              />
              <input
                type="color"
                value={styles.borderColor || '#E5E7EB'}
                onChange={(e) => handleStyleChange('borderColor', e.target.value)}
                className="h-10 rounded cursor-pointer"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};